/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving batches.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { useState, useEffect } from 'react';

import { bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */

import reducerRegistry from '../../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */

import * as Dtos from '../../../api/Neon.Dtos';

import client from '../../../api/client';


/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialBatchesState {
    batches: Dtos.Batch[] | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
};

export interface IBatchesState {
    batches: IInitialBatchesState;
};

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const BatchesInitialState: IInitialBatchesState = {
    batches: null,
    loadState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useBatchesSelector: TypedUseSelectorHook<IBatchesState> = useSelector;

export const BatchesSelectors = {
    selectBatches: (state: IBatchesState) => state.batches.batches,
    selectLoadState: (state: IBatchesState) => state.batches.loadState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class BatchesReducer extends ImmerReducer<IInitialBatchesState> {
    public load() {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(batches?: Dtos.Batch[]) {
        this.draftState.batches = batches ? batches : null;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.batches = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState = { ...BatchesInitialState };
    }
};

export const BatchesActions = createActionCreators(BatchesReducer);
const reducer = createReducerFunction(BatchesReducer, BatchesInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const BatchesApi = {
    load: () => {
        return client.get(new Dtos.GetBatches());
    }
};

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const BatchesLogic = {
    load: createLogic<IBatchesState, {}, undefined, string, ReturnType<typeof BatchesActions.load>>({
        type: BatchesActions.load.type,
        process: async ({ action }, dispatch, done) => {
            try {
                const response = await BatchesApi.load();

                dispatch(BatchesActions.loadSuccess(
                    response.batches
                ));
            }
            catch (error: any) {
                dispatch(BatchesActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    BatchesLogic.load
];

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const useBatches = (): [Dtos.Batch[] | null, IRequestState<Dtos.ResponseStatus>, typeof BatchesActions] => {
    const batches = useSelector(BatchesSelectors.selectBatches);
    const loadState = useSelector(BatchesSelectors.selectLoadState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof BatchesActions>(() => {
        return bindActionCreators(BatchesActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(BatchesActions, dispatch));
    }, [dispatch, setActions]);

    return [
        batches,
        loadState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('batches', reducer as any, logics as any);